// Persistent in-flight operations dock.
//
// Long jobs (publish, render, fleet install, image roll) used to be
// invisible the moment you left /operations/:id. This parks a small
// always-on readout in the bottom-right corner so the operator can start
// a 20-minute chromium publish, go look at the fleet, and still see it
// running. Completion toasts come from useOperationCompletionToasts in
// the same module — the dock only covers the "still going" half.
//
// Renders nothing when idle, so it costs no screen space in the common case.

import { Button } from "@veil/ui/components/button";
import { cn } from "@veil/ui/lib/utils";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import X from "lucide-react/dist/esm/icons/x";
import { useState } from "react";
import { Link } from "wouter";
import {
	type ActiveOperation,
	currentStepLabel,
	describeKind,
	useActiveOperations,
	useCancelOperation,
} from "@/lib/operations/use-active-operations";

export function OperationsDock() {
	const { data } = useActiveOperations();
	const [collapsed, setCollapsed] = useState(false);
	const cancel = useCancelOperation();

	const running = data ?? [];
	if (running.length === 0) return null;

	const summary = `${running.length} operation${running.length === 1 ? "" : "s"} running`;

	return (
		// z-40 keeps the dock above page content but under dialogs and the
		// toaster, so a confirm never lands behind it.
		<section
			aria-label="Running operations"
			className="fixed right-4 bottom-4 z-40 w-[min(22rem,calc(100vw-2rem))] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
		>
			{/* Only the count is announced (<output> is an implicit live
			    region). Elapsed times sit outside it so the 3s poll doesn't
			    nag a screen reader. */}
			<output className="sr-only">{summary}</output>

			<div className="overflow-hidden rounded-lg border bg-background/95 shadow-lg backdrop-blur supports-backdrop-filter:bg-background/80">
				<button
					type="button"
					onClick={() => setCollapsed((prev) => !prev)}
					aria-expanded={!collapsed}
					className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
				>
					<Loader2 className="size-4 shrink-0 animate-spin text-amber-600 dark:text-amber-400" />
					<span className="flex-1 font-medium">{summary}</span>
					<ChevronDown
						aria-hidden
						className={cn(
							"size-4 shrink-0 text-muted-foreground transition-transform",
							collapsed && "-rotate-90",
						)}
					/>
				</button>

				{collapsed ? null : (
					<ul className="max-h-72 divide-y overflow-y-auto border-t">
						{running.map((op) => (
							<OperationRow
								key={op.id}
								operation={op}
								onCancel={() => cancel.mutate(op.id)}
								cancelDisabled={cancel.isPending}
							/>
						))}
					</ul>
				)}
			</div>
		</section>
	);
}

function OperationRow({
	operation,
	onCancel,
	cancelDisabled,
}: {
	operation: ActiveOperation;
	onCancel: () => void;
	cancelDisabled: boolean;
}) {
	const step = currentStepLabel(operation);
	const kind = describeKind(operation.kind);

	return (
		<li className="flex items-start gap-2 px-3 py-2">
			<div className="min-w-0 flex-1">
				<Link
					href={`/operations/${operation.id}`}
					className="block truncate font-mono text-xs hover:underline"
					title={`${kind} — open detail`}
				>
					{operation.kind}
				</Link>
				<p className="truncate text-xs text-muted-foreground">
					{step ?? "Working…"}
				</p>
				{/* Indeterminate: pipelines append steps as they start, so
				    there is no honest total to fill a bar against. */}
				<div
					className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-muted"
					aria-hidden
				>
					<div className="h-full w-1/3 rounded-full bg-amber-500/70 motion-safe:animate-pulse" />
				</div>
			</div>
			<span className="shrink-0 pt-0.5 font-mono text-xs tabular-nums text-muted-foreground">
				{formatElapsed(operation.startedAt)}
			</span>
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={onCancel}
				disabled={cancelDisabled}
				aria-label={`Cancel ${kind}`}
				title={`Cancel ${kind}`}
			>
				<X />
			</Button>
		</li>
	);
}

/** `47s`, `4m03s`, `1h04m` — narrow enough to sit in a 22rem dock. */
function formatElapsed(startedAt: string): string {
	const start = Date.parse(startedAt);
	if (!Number.isFinite(start)) return "—";
	const sec = Math.max(0, Math.floor((Date.now() - start) / 1000));
	if (sec < 60) return `${sec}s`;
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m${String(sec % 60).padStart(2, "0")}s`;
	const hr = Math.floor(min / 60);
	return `${hr}h${String(min % 60).padStart(2, "0")}m`;
}
